import type {
  BookingStatusType,
  InventorySchema,
  OrderItemSchema,
  ResourceBookingSchema,
  ResourceInventorySchema,
} from '../../data/types';

export const REMAINING_INVENTORY_LOW_STOCK_RATIO = 0.15;

/** Statuses that reserve reusable inventory for a time window. */
export const ACTIVE_INVENTORY_RESERVATION_STATUSES: readonly BookingStatusType[] = [
  'approved',
  'in_progress',
];

export type InventoryStockStatus =
  | 'available'
  | 'low_stock'
  | 'out_of_stock'
  | 'in_use'
  | 'upcoming';

export type InventoryBookingLine = Pick<OrderItemSchema, 'id' | 'total_quantity'> & {
  inventory_id?: string;
};

/** Minimal booking shape used for reusable occupancy; full `ResourceBookingSchema` is assignable. */
export type InventoryStockBooking = Pick<
  ResourceBookingSchema,
  'id' | 'status' | 'start_time' | 'end_time'
> & {
  order_items?: InventoryBookingLine[];
  order_inventory?: InventoryBookingLine[];
};

export type InventoryTimeWindow = {
  start: Date | number | string;
  end: Date | number | string;
};

/** Date + clock times from a RequestKitchen booking selection. */
export type InventorySessionSelection = {
  date: string;
  startTime: string;
  endTime: string;
};

export type InventoryStockContextInput = {
  total_quantity: number;
  available_quantity?: number;
  is_reusable?: boolean;
  inventoryId?: string;
  bookings?: InventoryStockBooking[];
  now?: Date | number | string;
  /** Selected session windows. Used instead of wall-clock `now` for remaining qty. */
  sessions?: InventoryTimeWindow[];
};

export type ReusableInventoryAvailability = {
  reserved: number;
  available: number;
  hasUpcoming: boolean;
  status: InventoryStockStatus;
};

export const inventoryStockStatusColor: Record<InventoryStockStatus, string> = {
  available: '#059669',
  low_stock: '#ff8a00',
  out_of_stock: '#b84c4c',
  in_use: '#ff8a00',
  upcoming: '#2563eb',
};

export const inventoryStockStatusDotClass: Record<InventoryStockStatus, string> = {
  available: 'bg-[#059669]',
  low_stock: 'bg-[#ff8a00]',
  out_of_stock: 'bg-[#b84c4c]',
  in_use: 'bg-[#ff8a00]',
  upcoming: 'bg-[#2563eb]',
};

export const inventoryProductCardStockBadgeClass: Record<InventoryStockStatus, string> = {
  available: 'available',
  low_stock: 'low-stock',
  out_of_stock: 'out-of-stock',
  in_use: 'low-stock',
  upcoming: 'upcoming',
};

export const inventoryStockStatusLabel: Record<InventoryStockStatus, string> = {
  available: 'Available',
  low_stock: 'Low stock',
  out_of_stock: 'Out of stock',
  in_use: 'In Use',
  upcoming: 'Upcoming',
};

export const getInventoryAvailableQuantity = (
  totalQuantity: number,
  availableQuantity?: number
) => availableQuantity ?? totalQuantity;

export const getInventoryLowStockThreshold = (totalQuantity: number) => {
  const total = Math.max(totalQuantity, 0);
  if (total <= 0) return 0;
  return total * REMAINING_INVENTORY_LOW_STOCK_RATIO;
};

/** Parse a date-like value; invalid input returns null. */
const toTimeMs = (value?: Date | number | string | null): number | null => {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

/** Combine a booking-selection date and clock times into an overlap window. */
export const getInventorySessionWindow = (
  selection: InventorySessionSelection
): InventoryTimeWindow => ({
  start: `${selection.date}T${selection.startTime}`,
  end: `${selection.date}T${selection.endTime}`,
});

/** Map booking selections to session windows. */
export const getInventorySessionWindows = (
  selections: InventorySessionSelection[] | null | undefined
): InventoryTimeWindow[] => {
  if (!selections?.length) return [];
  return selections.map(getInventorySessionWindow);
};

/** Order lines on a booking, preferring fulfilled `order_items`. */
export const getBookingInventoryLines = (
  booking?: InventoryStockBooking | null
): InventoryBookingLine[] => booking?.order_items ?? booking?.order_inventory ?? [];

/** Whether a line belongs to the given inventory item. */
export const inventoryLineMatchesItem = (
  line: InventoryBookingLine | null | undefined,
  inventoryId?: string
): boolean => {
  if (!inventoryId || !line) return false;
  return line.inventory_id === inventoryId || line.id === inventoryId;
};

/** Whether a booking includes the inventory item on any order line. */
export const bookingIncludesInventory = (
  booking: InventoryStockBooking | null | undefined,
  inventoryId?: string
): boolean => {
  if (!inventoryId || !booking) return false;
  return getBookingInventoryLines(booking).some((line) => inventoryLineMatchesItem(line, inventoryId));
};

/** Qty of the inventory item on a booking's order lines. */
export const getBookingInventoryQuantity = (
  booking: InventoryStockBooking | null | undefined,
  inventoryId?: string
): number => {
  if (!inventoryId || !booking) return 0;
  return getBookingInventoryLines(booking).reduce((sum, line) => {
    if (!inventoryLineMatchesItem(line, inventoryId)) return sum;
    const qty = Number(line.total_quantity);
    return sum + (Number.isFinite(qty) && qty > 0 ? qty : 0);
  }, 0);
};

/** Approved / in-progress bookings reserve reusable stock; pending, canceled, and completed do not. */
export const isActiveInventoryReservation = (
  status?: BookingStatusType | string | null
): boolean => {
  if (!status) return false;
  return (ACTIVE_INVENTORY_RESERVATION_STATUSES as readonly string[]).includes(
    String(status).toLowerCase()
  );
};

/** Inclusive overlap of booking `[start_time, end_time]` with the evaluation window. */
export const bookingOverlapsWindow = (
  booking: InventoryStockBooking | null | undefined,
  windowStart: Date | number | string,
  windowEnd: Date | number | string
): boolean => {
  if (!booking) return false;
  const bookingStart = toTimeMs(booking.start_time);
  const bookingEnd = toTimeMs(booking.end_time);
  const startMs = toTimeMs(windowStart);
  const endMs = toTimeMs(windowEnd);
  if (bookingStart == null || bookingEnd == null || startMs == null || endMs == null) {
    return false;
  }
  return bookingStart <= endMs && bookingEnd >= startMs;
};

/** Bookings (any status) whose order lines include the inventory item. */
export const getInventoryBookingsForItem = <T extends InventoryStockBooking>(
  bookings: T[] | null | undefined,
  inventoryId?: string
): T[] => {
  if (!inventoryId || !bookings?.length) return [];
  return bookings.filter((booking) => bookingIncludesInventory(booking, inventoryId));
};

/** Sum of overlapping active reservation qty for an inventory item in a time window. */
export const getReservedInventoryQuantity = (
  bookings: InventoryStockBooking[] | null | undefined,
  inventoryId: string | undefined,
  window: InventoryTimeWindow
): number => {
  if (!inventoryId || !bookings?.length) return 0;
  return bookings.reduce((sum, booking) => {
    if (!isActiveInventoryReservation(booking.status)) return sum;
    if (!bookingIncludesInventory(booking, inventoryId)) return sum;
    if (!bookingOverlapsWindow(booking, window.start, window.end)) return sum;
    return sum + getBookingInventoryQuantity(booking, inventoryId);
  }, 0);
};

/** Remaining reusable qty for one session window. */
export const getReusableRemainingForWindow = (
  totalQuantity: number,
  bookings: InventoryStockBooking[] | null | undefined,
  inventoryId: string | undefined,
  window: InventoryTimeWindow
): number => {
  const total = Math.max(totalQuantity, 0);
  return Math.max(total - getReservedInventoryQuantity(bookings, inventoryId, window), 0);
};

/**
 * Remaining qty for booking UI.
 * Reusable items use overlapping active reservations on selected session windows
 * (min remaining across multi-session picks). Non-reusable items use stored qty.
 * Does not use wall-clock now.
 */
export const getInventoryRemainingForSessions = (
  input: InventoryStockContextInput
): number => {
  const total = Math.max(input.total_quantity, 0);
  if (!input.is_reusable) {
    return Math.max(getInventoryAvailableQuantity(total, input.available_quantity), 0);
  }
  if (!input.sessions?.length) {
    return total;
  }
  return Math.min(
    ...input.sessions.map((session) =>
      getReusableRemainingForWindow(total, input.bookings, input.inventoryId, session)
    )
  );
};

/** Out of stock when remaining for the selected session(s) is 0. */
export const isInventoryOutOfStockForSessions = (input: InventoryStockContextInput): boolean =>
  getInventoryRemainingForSessions(input) <= 0;

/** Flatten `resource_inventory` maps into a single catalog list. */
export const flattenResourceInventoryCatalog = (
  resourceInventory?: ResourceInventorySchema | null | unknown
): InventorySchema[] => {
  if (!resourceInventory || typeof resourceInventory !== 'object' || Array.isArray(resourceInventory)) {
    return [];
  }
  return Object.values(resourceInventory as ResourceInventorySchema).flatMap((items) =>
    Array.isArray(items) ? items : []
  );
};

/** Prefer catalog stock fields when an order line has overwritten `total_quantity`. */
export const resolveInventoryStockFields = (
  item: {
    id?: string;
    inventory_id?: string;
    total_quantity: number;
    available_quantity?: number;
    is_reusable?: boolean;
  },
  catalogItems?: Array<{
    id?: string;
    total_quantity: number;
    available_quantity?: number;
    is_reusable?: boolean;
  }>
) => {
  const inventoryId = item.inventory_id || item.id;
  const catalogItem = inventoryId
    ? catalogItems?.find((catalog) => catalog.id === inventoryId)
    : undefined;
  return {
    id: inventoryId,
    inventory_id: inventoryId,
    total_quantity: catalogItem?.total_quantity ?? item.total_quantity,
    available_quantity: catalogItem?.available_quantity ?? item.available_quantity,
    is_reusable: catalogItem?.is_reusable ?? item.is_reusable,
  };
};

/** Catalog/order item remaining for the selected session windows. */
export const getInventoryItemRemainingForSessions = (
  item: {
    id?: string;
    inventory_id?: string;
    total_quantity: number;
    available_quantity?: number;
    is_reusable?: boolean;
  },
  bookings?: InventoryStockBooking[],
  sessions?: InventoryTimeWindow[],
  catalogItems?: Array<{
    id?: string;
    total_quantity: number;
    available_quantity?: number;
    is_reusable?: boolean;
  }>
): number => {
  const stockItem = resolveInventoryStockFields(item, catalogItems);
  return getInventoryRemainingForSessions({
    total_quantity: stockItem.total_quantity,
    available_quantity: stockItem.available_quantity,
    is_reusable: stockItem.is_reusable,
    inventoryId: stockItem.inventory_id,
    bookings,
    sessions,
  });
};

/** Whether an active booking for the item starts after `now` and does not overlap now. */
export const hasUpcomingInventoryReservation = (
  bookings: InventoryStockBooking[] | null | undefined,
  inventoryId: string | undefined,
  now: Date | number | string
): boolean => {
  if (!inventoryId || !bookings?.length) return false;
  const nowMs = toTimeMs(now);
  if (nowMs == null) return false;
  return bookings.some((booking) => {
    if (!isActiveInventoryReservation(booking.status)) return false;
    if (!bookingIncludesInventory(booking, inventoryId)) return false;
    if (bookingOverlapsWindow(booking, nowMs, nowMs)) return false;
    const startMs = toTimeMs(booking.start_time);
    return startMs != null && startMs > nowMs;
  });
};

/**
 * Reusable availability from overlapping active bookings at `now`.
 * Ignores stored `available_quantity`. Missing bookings means nothing reserved.
 */
export const getReusableInventoryAvailability = ({
  total_quantity,
  inventoryId,
  bookings,
  now,
}: Pick<InventoryStockContextInput, 'total_quantity' | 'inventoryId' | 'bookings' | 'now'>): ReusableInventoryAvailability => {
  const total = Math.max(total_quantity, 0);
  const at = now ?? Date.now();
  const reserved = getReservedInventoryQuantity(bookings, inventoryId, { start: at, end: at });
  const available = Math.max(total - reserved, 0);
  const hasUpcoming = hasUpcomingInventoryReservation(bookings, inventoryId, at);

  let status: InventoryStockStatus = 'available';
  if (total >= 1 && reserved >= total) {
    status = 'out_of_stock';
  } else if (reserved > 0 && reserved < total) {
    status = 'in_use';
  } else if (hasUpcoming) {
    status = 'upcoming';
  }

  return { reserved, available, hasUpcoming, status };
};

/** Effective remaining qty: booking occupancy for reusable items, stored qty otherwise. */
export const getEffectiveInventoryAvailableQuantity = (
  input: InventoryStockContextInput
): number => {
  const total = Math.max(input.total_quantity, 0);
  if (input.is_reusable) {
    if (input.sessions?.length) {
      return getInventoryRemainingForSessions(input);
    }
    return getReusableInventoryAvailability(input).available;
  }
  return Math.max(getInventoryAvailableQuantity(total, input.available_quantity), 0);
};

export const isInventoryInUse = (input: InventoryStockContextInput) =>
  getInventoryStockStatusForItem(input) === 'in_use';

export const getInventoryStockStatus = (
  totalQuantity: number,
  availableQuantity?: number,
  isReusable?: boolean
): InventoryStockStatus => {
  const total = Math.max(totalQuantity, 0);
  const available = Math.max(getInventoryAvailableQuantity(total, availableQuantity), 0);

  if (isReusable) {
    if (total >= 1 && available <= 0) {
      return 'out_of_stock';
    }
    if (total >= 1 && available < total) {
      return 'in_use';
    }
    return 'available';
  }

  if (available <= 0) {
    return 'out_of_stock';
  }

  if (total <= 0) {
    return 'available';
  }

  const lowStockThreshold = getInventoryLowStockThreshold(total);
  if (available <= lowStockThreshold) {
    return 'low_stock';
  }

  return 'available';
};

export const getInventoryStockStatusForItem = (
  input: InventoryStockContextInput
): InventoryStockStatus => {
  if (input.is_reusable) {
    if (input.sessions?.length) {
      const remaining = getInventoryRemainingForSessions(input);
      const total = Math.max(input.total_quantity, 0);
      if (total >= 1 && remaining <= 0) return 'out_of_stock';
      if (total >= 1 && remaining < total) return 'in_use';
      return 'available';
    }
    return getReusableInventoryAvailability(input).status;
  }
  return getInventoryStockStatus(input.total_quantity, input.available_quantity, false);
};

export type InventoryStockVisualStatus = Exclude<InventoryStockStatus, 'in_use'>;

export const getInventoryStockVisualStatus = (
  input: InventoryStockContextInput
): InventoryStockVisualStatus => {
  const status = getInventoryStockStatusForItem(input);
  const available = getEffectiveInventoryAvailableQuantity(input);

  if (status === 'in_use') {
    if (available <= 0) {
      return 'out_of_stock';
    }
    return 'low_stock';
  }

  return status;
};

export const getInventoryStockStatusLabel = (input: InventoryStockContextInput): string =>
  inventoryStockStatusLabel[getInventoryStockStatusForItem(input)];

export const getInventoryStockStatusColorForItem = (
  input: InventoryStockContextInput
): string => inventoryStockStatusColor[getInventoryStockVisualStatus(input)];

export const getInventoryProductCardStockBadgeClass = (
  input: InventoryStockContextInput
): string => inventoryProductCardStockBadgeClass[getInventoryStockVisualStatus(input)];

export const getInventoryStockStatusDot = (
  input: InventoryStockContextInput
): InventoryStockVisualStatus => getInventoryStockVisualStatus(input);

export const getInventoryUsedQuantity = (
  totalQuantity: number,
  availableQuantity?: number
) => Math.max(totalQuantity - getInventoryAvailableQuantity(totalQuantity, availableQuantity), 0);

export const getInventoryUsedQuantityForItem = (input: InventoryStockContextInput) => {
  const total = Math.max(input.total_quantity, 0);
  return Math.max(total - getEffectiveInventoryAvailableQuantity(input), 0);
};

export const getInventoryUsedPercent = (
  totalQuantity: number,
  availableQuantity?: number
) => {
  const total = Math.max(totalQuantity, 0);
  if (total <= 0) return 0;
  const used = getInventoryUsedQuantity(total, availableQuantity);
  return Math.min(Math.round((used / total) * 100), 100);
};

export const getInventoryUsedPercentForItem = (input: InventoryStockContextInput) => {
  const total = Math.max(input.total_quantity, 0);
  if (total <= 0) return 0;
  const used = getInventoryUsedQuantityForItem(input);
  return Math.min(Math.round((used / total) * 100), 100);
};

export const getInventoryStockProgressFillClass = (
  status: InventoryStockStatus,
  usedPercent: number
) => {
  if (usedPercent <= 0) {
    return inventoryStockStatusDotClass.available;
  }

  if (status === 'out_of_stock' || usedPercent >= 100) {
    return inventoryStockStatusDotClass.out_of_stock;
  }

  if (status === 'upcoming') {
    return inventoryStockStatusDotClass.upcoming;
  }

  return 'bg-[#CE5D5D]';
};

export const getInventoryStockProgressFillClassForItem = (
  input: InventoryStockContextInput
) => {
  const usedPercent = getInventoryUsedPercentForItem(input);

  if (usedPercent <= 0) {
    return inventoryStockStatusDotClass.available;
  }

  const status = getInventoryStockStatusForItem(input);
  const visualStatus = getInventoryStockVisualStatus(input);

  if (status === 'in_use' || status === 'upcoming') {
    return inventoryStockStatusDotClass[visualStatus];
  }

  if (visualStatus === 'out_of_stock' || usedPercent >= 100) {
    return inventoryStockStatusDotClass.out_of_stock;
  }

  return 'bg-[#CE5D5D]';
};
